import React, { useRef, useState, type RefObject } from 'react';
import { useIntl } from 'react-intl';
import { Modal, MODAL_SIZE_LG } from '@aibuild/ui';
import LlmModelPicker from '@components/Chat/LlmModelPicker';
import PromptComposer from '@components/PromptComposer';
import { useChatInputLimits } from '@hooks/useChatInputLimits';
import { useFileAttachments } from '@hooks/useFileAttachments';
import { LLM_MODEL_AUTO } from '@utils/llmModel';
import CreateAppExamples from './CreateAppExamples';
import CreateAppHero from './CreateAppHero';
import { CREATE_APP_TITLE } from './CreateAppHero.constants';
import SkillPickerChip from './SkillPickerChip';
import { useCreateAppFromPrompt } from './useCreateAppFromPrompt';
import { DialogBody, FooterChips } from './CreateAppDialog.styled';

interface CreateAppFormProps {
  readonly inputRef: RefObject<HTMLTextAreaElement | null>;
}

const CreateAppForm: React.FC<CreateAppFormProps> = ({ inputRef }) => {
  const intl = useIntl();
  const { create, creating } = useCreateAppFromPrompt();
  const { maxAttachmentsPerMessage, maxMessageLengthChars } = useChatInputLimits();
  const attachments = useFileAttachments({ maxFiles: maxAttachmentsPerMessage });
  const [text, setText] = useState('');
  const [allowedSkills, setAllowedSkills] = useState<string[]>([]);
  const [llmModel, setLlmModel] = useState(LLM_MODEL_AUTO);

  const build = (brief: string, exampleName?: string) => {
    create({ text: brief, files: attachments.pendingFiles, exampleName, allowedSkills, llmModel })
      .then((opened) => {
        if (!opened) inputRef.current?.focus();
      })
      .catch(() => undefined);
  };

  return (
    <DialogBody>
      <CreateAppHero />
      <PromptComposer
        value={text}
        onChange={setText}
        onSubmit={() => build(text)}
        canSubmit={text.trim().length > 0}
        busy={creating}
        attachments={attachments}
        maxLength={maxMessageLengthChars}
        inputRef={inputRef}
        placeholder={intl.formatMessage({
          id: 'apps.builder.create.prompt.placeholder',
          defaultMessage: 'Describe the app you want...',
        })}
        ariaLabel={intl.formatMessage({ id: 'apps.builder.create.prompt.label', defaultMessage: 'Describe your app' })}
        attachLabel={intl.formatMessage({ id: 'apps.builder.create.attach', defaultMessage: 'Add sample files' })}
        sendLabel={intl.formatMessage({ id: 'apps.builder.create.send', defaultMessage: 'Build my app' })}
        dataTestId="create-app"
        footer={
          <FooterChips>
            <SkillPickerChip selected={allowedSkills} onChange={setAllowedSkills} disabled={creating} />
            <LlmModelPicker llmModel={llmModel} onPick={setLlmModel} disabled={creating} menuDirection="down" />
          </FooterChips>
        }
      />
      <CreateAppExamples
        onPick={(example) => build(intl.formatMessage(example.brief), intl.formatMessage(example.name))}
        disabled={creating}
      />
    </DialogBody>
  );
};

interface CreateAppDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const CreateAppDialog: React.FC<CreateAppDialogProps> = ({ open, onClose }) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={MODAL_SIZE_LG}
      ariaLabel={intl.formatMessage(CREATE_APP_TITLE)}
      initialFocusRef={inputRef}
      dataTestId="create-app-dialog"
    >
      <CreateAppForm inputRef={inputRef} />
    </Modal>
  );
};

export default CreateAppDialog;
